import type { StandardSchemaV1 } from '@standard-schema/spec';
import { useEffect, useMemo } from 'react';
import { validate, validateUnsafe } from '../../utils/validate';

export const useValidatedStorage = <
    TSchema,
    TSetter extends (value: TSchema) => void,
>({
    schema,
    defaultValue,
    useStorage,
}: {
    schema: StandardSchemaV1<TSchema>;
    defaultValue: NoInfer<TSchema>;
    useStorage: () => [NoInfer<TSchema>, TSetter, ...Array<unknown>];
}) => {
    const validDefaultValue = useMemo(
        () => validateUnsafe(schema, defaultValue),
        [defaultValue, schema]
    );

    const [state, setState] = useStorage();

    const validation = useMemo(() => validate(schema, state), [schema, state]);

    useEffect(() => {
        if (!validation.success) {
            setState(validDefaultValue);
        }
    }, [validDefaultValue, setState, validation.success]);

    const validState = useMemo(
        () => (validation.success ? validation.data : validDefaultValue),
        [validDefaultValue, validation.data, validation.success]
    );

    return [validState, setState] as const;
};
